import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import dayjs from "dayjs";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { setHeaderCollapse } from "../../../../core/data/redux/commonSlice";
import { AuthContext } from "../../../../context/authcontext";
import { all_routes } from "../../../router/all_routes";
import {
  HeaderAction,
  ExportDropdown,
  ExportKind,
  OptionDropdown,
  DropdownOption,
  useTemplateFont,
  useWheelZoom,
  TEMPLATE_FONT_FAMILY,
} from "../common";
import { GranularityToggle } from "../chartControls";
import DashboardFilterDropdown, {
  ActiveFilterChips,
  useDashboardFilters,
  hasFilterChips,
  describeFilters,
  toApiFilters,
  defaultDateRange,
} from "../filters/DashboardFilterDropdown";
import { downloadExportFile } from "../dashboardApi";
import { useMoneyFormat, MoneyFormat } from "../../../../components/currencyFormat";
import { getLifecycleLabels, LifecycleLabels } from "../../../../utils/lifecycleUtils";
import { useSalesUserId } from "../../../../utils/salesUserScope";
import { exportDashboardPdf } from "../exportDashboardPdf";
import { PersonAvatar } from "../dashboard/DashboardParts";
import {
  fetchSalesOverview,
  exportSalesOverviewExcel,
  ConversionKey,
  SalesOverview,
  SalesOverviewStat,
  SalesPeriod,
  RecentDeal,
} from "./salesOverviewService";
import {
  palette,
  StatMeta,
  overviewStatMeta,
  defaultStatMeta,
  stageClassNames,
  SALES_TREND_POINTS,
  GAUGE_SEGMENTS,
} from "./constants";
import SalesSummarySkeleton from "./SalesSummarySkeleton";
import "./salesSummary.css";

const route = all_routes;
const DATE_FORMAT = "DD MMM YYYY";
const GROWTH_CHART_ID = "ss-sales-growth";
const DEAL_TITLE_MAX_LENGTH = 34;

const finite = (value: number) => (Number.isFinite(value) ? value : 0);

const countFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

const trimDecimals = (value: number, digits: number) => String(Number(finite(value).toFixed(digits)));

const formatAxisValue = (value: number) => {
  const amount = finite(value);
  const abs = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";
  if (abs >= 1e9) return `${sign}${trimDecimals(abs / 1e9, 1)}B`;
  if (abs >= 1e6) return `${sign}${trimDecimals(abs / 1e6, 1)}M`;
  if (abs >= 1e3) return `${sign}${trimDecimals(abs / 1e3, 1)}k`;
  return `${sign}${trimDecimals(abs, 2)}`;
};

const parseGrowth = (growth?: string | null) => {
  const parsed = parseFloat(String(growth ?? ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const isNegativeGrowth = (growth?: string | null) => (parseGrowth(growth) ?? 0) < 0;

const formatGrowth = (growth?: string | null) => {
  const text = String(growth ?? "").trim();
  if (!text) return "0%";
  if (/^[+-]/.test(text)) return text;
  return (parseGrowth(text) ?? 0) > 0 ? `+${text}` : text;
};

const growthTextClass = (growth?: string | null) => (isNegativeGrowth(growth) ? "text-danger" : "text-success");

const growthBadgeClass = (growth?: string | null) =>
  isNegativeGrowth(growth) ? "badge-soft-danger" : "badge-soft-success";

const statusClassName = (status: string) => {
  const normalized = status.toLowerCase();
  if (normalized === "won") return "badge-soft-success";
  if (normalized === "lost") return "badge-soft-danger";
  return "badge-soft-info";
};

const statIcon = (meta: StatMeta, money: MoneyFormat) => {
  if (meta.currencyIcon === "coin") return money.coinIcon;
  if (meta.currencyIcon === "filter") return money.filterIcon;
  return meta.icon;
};

const formatStatValue = (stat: SalesOverviewStat, meta: StatMeta, money: MoneyFormat) =>
  meta.format === "currency"
    ? money.short(stat.amount ?? 0)
    : countFormatter.format(stat.count ?? stat.amount ?? 0);

const spansMultipleYears = (points: { date: string }[]) =>
  new Set(points.map((point) => point.date.slice(0, 4))).size > 1;

const pointLabels = (points: { date: string }[], period: SalesPeriod) => {
  const multiYear = spansMultipleYears(points);
  return points.map((point) => {
    if (period === "by_year") return point.date;
    const parsed = dayjs(point.date);
    if (!parsed.isValid()) return point.date;
    if (period === "by_week") return parsed.format(multiYear ? "DD MMM YY" : "DD MMM");
    return parsed.format(multiYear ? "MMM YYYY" : "MMM");
  });
};

const MAX_AXIS_TICKS = 6;

const niceScale = (value: number) => {
  if (!(value > 0)) return { max: 5, tickAmount: 5 };
  const magnitude = Math.pow(10, Math.floor(Math.log10(value / MAX_AXIS_TICKS)));
  for (const multiplier of [1, 2, 2.5, 5, 10, 20]) {
    const step = multiplier * magnitude;
    const ticks = Math.max(2, Math.ceil(value / step - 1e-9));
    if (ticks <= MAX_AXIS_TICKS) return { max: Number((ticks * step).toPrecision(12)), tickAmount: ticks };
  }
  return { max: value, tickAmount: MAX_AXIS_TICKS };
};

const salesBarOptions = (labels: string[], max: number, formatAmount: (value: number) => string): ApexOptions => ({
  chart: {
    type: "bar",
    toolbar: { show: false },
    zoom: { enabled: false },
    foreColor: palette.axis,
    fontFamily: TEMPLATE_FONT_FAMILY,
    parentHeightOffset: 0,
  },
  colors: [palette.secondary],
  fill: {
    type: "gradient",
    gradient: {
      type: "vertical",
      shadeIntensity: 0,
      gradientToColors: [palette.salesBarLight],
      opacityFrom: 1,
      opacityTo: 1,
      stops: [0, 100],
    },
  },
  plotOptions: { bar: { columnWidth: "55%", borderRadius: 5, borderRadiusApplication: "end" } },
  dataLabels: { enabled: false },
  states: { hover: { filter: { type: "darken" } }, active: { filter: { type: "none" } } },
  xaxis: {
    categories: labels,
    labels: { rotate: 0, hideOverlappingLabels: true, style: { fontSize: "11px" } },
    axisBorder: { show: false },
    axisTicks: { show: false },
    tooltip: { enabled: false },
  },
  yaxis: { show: false, min: 0, max: Math.max(max, 1) },
  grid: { show: false, padding: { top: -12, right: 0, bottom: -4, left: 0 } },
  legend: { show: false },
  tooltip: {
    x: { show: true },
    marker: { show: false },
    y: { title: { formatter: () => "Sales" }, formatter: (val: number) => formatAmount(val) },
  },
});

const categoryXAxis = (labels: string[]): ApexXAxis => ({
  categories: labels,
  tickPlacement: "on",
  tickAmount: labels.length > 12 ? 12 : undefined,
  axisBorder: { show: true, color: palette.grid },
  axisTicks: { show: true, color: palette.grid },
  labels: { rotate: 0, hideOverlappingLabels: true, style: { fontSize: "11px" } },
  tooltip: { enabled: false },
});

const GAUGE_CENTER_X = 176;
const GAUGE_CENTER_Y = 176;

const gaugePoint = (radius: number, degrees: number) => {
  const radians = (degrees * Math.PI) / 180;
  return { x: GAUGE_CENTER_X + radius * Math.cos(radians), y: GAUGE_CENTER_Y - radius * Math.sin(radians) };
};

const ConversionGauge = ({ value }: { value: number }) => {
  const clamped = Math.min(100, Math.max(0, finite(value)));
  const filled = Math.round((clamped / 100) * GAUGE_SEGMENTS);
  const step = 180 / GAUGE_SEGMENTS;
  const needleAngle = 180 - clamped * 1.8;
  const tip = gaugePoint(72, needleAngle);
  const left = gaugePoint(2.5, needleAngle + 90);
  const right = gaugePoint(2.5, needleAngle - 90);
  return (
    <svg
      className="ss-gauge"
      viewBox="0 0 352 186"
      width={352}
      height={186}
      role="img"
      aria-label={`Conversion rate ${trimDecimals(clamped, 1)}%`}
    >
      {Array.from({ length: GAUGE_SEGMENTS }, (_, index) => {
        const angle = 180 - step / 2 - index * step;
        const inner = gaugePoint(120, angle);
        const outer = gaugePoint(163, angle);
        return (
          <line
            key={index}
            x1={inner.x}
            y1={inner.y}
            x2={outer.x}
            y2={outer.y}
            stroke={index < filled ? palette.primary : palette.gaugeTrack}
            strokeWidth={14}
            strokeLinecap="round"
          />
        );
      })}
      {[0, 20, 40, 60, 80, 100].map((tick) => {
        const point = gaugePoint(84, 180 - tick * 1.8);
        return (
          <text
            key={tick}
            x={point.x}
            y={point.y}
            textAnchor="middle"
            dominantBaseline="central"
            fill={palette.gaugeLabel}
            fontSize={12}
            fontWeight={600}
            fontFamily={TEMPLATE_FONT_FAMILY}
          >
            {tick}
          </text>
        );
      })}
      <polygon points={`${left.x},${left.y} ${tip.x},${tip.y} ${right.x},${right.y}`} fill={palette.needle} />
      <circle cx={GAUGE_CENTER_X} cy={GAUGE_CENTER_Y} r={5} fill={palette.needle} />
    </svg>
  );
};

const DealsRings = ({ won, lost, names }: { won: number; lost: number; names: LifecycleLabels }) => {
  const total = won + lost;
  const rings = [
    { key: "won", label: `${names.deals} Won`, count: won, color: palette.won, radius: 59 },
    { key: "lost", label: `${names.deals} Lost`, count: lost, color: palette.lost, radius: 36 },
  ];
  return (
    <svg className="ss-deals-rings" viewBox="0 0 130 130" width={130} height={130} role="img" aria-label={`${names.deals} won vs lost`}>
      {rings.map((ring) => {
        const circumference = 2 * Math.PI * ring.radius;
        const share = total > 0 ? ring.count / total : 0;
        return (
          <g key={ring.key}>
            <title>{`${ring.label}: ${countFormatter.format(ring.count)} (${trimDecimals(share * 100, 1)}%)`}</title>
            <circle cx={65} cy={65} r={ring.radius} fill="none" stroke={palette.ringTrack} strokeWidth={10} />
            {share > 0 && (
              <circle
                cx={65}
                cy={65}
                r={ring.radius}
                fill="none"
                stroke={ring.color}
                strokeWidth={10}
                strokeLinecap="round"
                strokeDasharray={`${circumference * share} ${circumference}`}
                transform="rotate(-90 65 65)"
              />
            )}
          </g>
        );
      })}
    </svg>
  );
};

const SalesTotalBlock = ({
  value,
  growth,
  chart,
}: {
  value: string;
  growth: string;
  chart: React.ReactNode;
}) => (
  <div className="ss-till-date ss-till-date-mtd d-flex">
    <div className="ss-till-date-tag d-flex align-items-center position-relative">
      <i className="ti ti-chart-bar fs-20 text-white" />
      <span className="ss-arrow" />
    </div>
    <div className="ss-till-date-panel flex-fill min-w-0 p-3 d-flex flex-wrap flex-md-nowrap gap-4">
      <div className="d-flex flex-column justify-content-center flex-shrink-0 ss-sales-total">
        <p className="text-dark mb-2">Total Sales</p>
        <h3 className="ss-till-date-value text-truncate mb-3">{value}</h3>
        <div className="d-flex align-items-center gap-1 flex-wrap">
          <span className={`badge rounded-pill ${growthBadgeClass(growth)}`}>{formatGrowth(growth)}</span>
          <p className="mb-0">vs Last Period</p>
        </div>
      </div>
      <div className="flex-fill min-w-0 ss-sales-bars">{chart}</div>
    </div>
  </div>
);

const DealTitle = ({ deal, names }: { deal: RecentDeal; names: LifecycleLabels }) => {
  const title = deal.title || `Untitled ${names.deal}`;
  const truncated = title.length > DEAL_TITLE_MAX_LENGTH;
  const text = truncated ? `${title.slice(0, DEAL_TITLE_MAX_LENGTH).trimEnd()}...` : title;
  const content =
    deal.dealId !== null ? (
      <Link to={`/deals/${deal.dealId}`} className="ss-deal-title">
        {text}
      </Link>
    ) : (
      <span className="ss-deal-title">{text}</span>
    );
  if (!truncated) return content;
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip id={`ss-deal-${deal.dealId ?? title}`}>{title}</Tooltip>}>
      {content}
    </OverlayTrigger>
  );
};

const EmptyState = ({ message }: { message: string }) => (
  <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 ss-empty">
    <i className="ti ti-chart-bar-off fs-1 mb-2" />
    <p className="mb-0">{message}</p>
  </div>
);

const SalesSummary = () => {
  useTemplateFont();
  const dispatch = useDispatch();
  const headerCollapse = useSelector((state: any) => state.commonSlice.headerCollapse);
  const { selectedTenantId, lifecycleTypeNames } = useContext(AuthContext);
  const names = useMemo(() => getLifecycleLabels(lifecycleTypeNames), [lifecycleTypeNames]);
  const salesUserId = useSalesUserId();

  const [data, setData] = useState<SalesOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const { filters, setFilters, defaults: defaultFilters } = useDashboardFilters();
  const [exporting, setExporting] = useState<ExportKind | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);
  const growthZoomRef = useWheelZoom(GROWTH_CHART_ID);

  const [salesKey, setSalesKey] = useState<SalesPeriod>("by_week");
  const [conversionKey, setConversionKey] = useState<ConversionKey>("deal");
  const [growthKey, setGrowthKey] = useState<SalesPeriod>("by_month");

  const apiFilters = useMemo(() => ({ ...toApiFilters(filters), salesUserId }), [filters, salesUserId]);
  const money = useMoneyFormat(filters.currencyCode);
  const formatAmount = money.amount;
  const formatDealValue = money.whole;

  useEffect(() => {
    setData(null);
  }, [selectedTenantId]);

  useEffect(() => {
    if (!selectedTenantId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchSalesOverview(selectedTenantId, apiFilters)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Failed to load sales overview");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTenantId, reloadKey, apiFilters]);

  const reload = () => setReloadKey((key) => key + 1);

  const handleExport = async (kind: ExportKind) => {
    const container = reportRef.current;
    if (!selectedTenantId || !data || exporting) return;
    setExporting(kind);
    try {
      if (kind === "pdf") {
        if (!container) return;
        await new Promise((resolve) => setTimeout(resolve, 150));
        await exportDashboardPdf({
          container,
          sectionSelector: ".ss-pdf-section",
          title: "Sales Dashboard",
          filtersText: describeFilters(filters, true),
          fileName: `sales-overview-${dayjs().format("YYYY-MM-DD")}.pdf`,
        });
      } else {
        const file = await exportSalesOverviewExcel(selectedTenantId, apiFilters);
        await downloadExportFile(file, `sales-overview-${dayjs().format("YYYY-MM-DD")}`);
      }
    } catch (err) {
      setError((err as Error)?.message || `Failed to export ${kind === "pdf" ? "PDF" : "Excel"}`);
    } finally {
      setExporting(null);
    }
  };

  const rangeLabel = useMemo(() => {
    const range = filters.startDate && filters.endDate ? { startDate: filters.startDate, endDate: filters.endDate } : defaultDateRange();
    return `${dayjs(range.startDate).format(DATE_FORMAT)} - ${dayjs(range.endDate).format(DATE_FORMAT)}`;
  }, [filters.startDate, filters.endDate]);

  const stats = data?.overviewStats ?? [];
  const statByKey = (key: string) => stats.find((stat) => stat.key === key);
  const totalSales = statByKey("total_sales_in_current_period");

  const salesBars = useMemo(() => {
    const points = (data?.salesGrowth[salesKey] ?? []).slice(-SALES_TREND_POINTS);
    const values = points.map((point) => point.actual);
    return {
      values,
      options: salesBarOptions(pointLabels(points, salesKey), Math.max(0, ...values), formatAmount),
    };
  }, [data, salesKey, formatAmount]);

  const conversionOptions = useMemo<DropdownOption<ConversionKey>[]>(
    () => [
      { value: "lead", label: names.leads },
      { value: "pipeline", label: names.pipelines },
      { value: "deal", label: names.deals },
    ],
    [names]
  );
  const conversionValue = data?.conversionRate[conversionKey] ?? 0;
  const conversionLabel = conversionOptions.find((option) => option.value === conversionKey)?.label ?? names.deals;

  const wonLost = data?.dealsWonVsLost;
  const closedCount = (wonLost?.won.count ?? 0) + (wonLost?.lost.count ?? 0);
  const pipeline = data?.pipeline;

  const stageWidths = useMemo(() => {
    const stages = data?.pipeline.stages ?? [];
    const reference = data?.pipeline.total || Math.max(0, ...stages.map((stage) => stage.value));
    return stages.map((stage) => Math.min(100, Math.max(0, reference > 0 ? (stage.value / reference) * 100 : 0)));
  }, [data]);

  const deals = data?.recentDeals ?? [];
  const avgDeal = data?.avgDeal;
  const avgDealMax = Math.max(avgDeal?.current ?? 0, avgDeal?.previous ?? 0);
  const avgDealChange = (avgDeal?.current ?? 0) - (avgDeal?.previous ?? 0);
  const recentDealValues = deals.map((deal) => deal.value).filter((value) => Number.isFinite(value));
  const avgDealTiles = [
    {
      key: "difference",
      label: "Difference",
      value: `${avgDealChange < 0 ? "-" : "+"}${formatAmount(Math.abs(avgDealChange))}`,
      className: avgDealChange < 0 ? "text-danger" : "text-success",
    },
    {
      key: "largest",
      label: `Largest Recent ${names.deal}`,
      value: recentDealValues.length ? formatDealValue(Math.max(...recentDealValues)) : "-",
      className: "text-dark",
    },
    {
      key: "smallest",
      label: `Smallest Recent ${names.deal}`,
      value: recentDealValues.length ? formatDealValue(Math.min(...recentDealValues)) : "-",
      className: "text-dark",
    },
  ];

  const avgDealOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        zoom: { enabled: false },
        foreColor: palette.axis,
        fontFamily: TEMPLATE_FONT_FAMILY,
        parentHeightOffset: 0,
      },
      colors: [palette.dealSize, palette.dealSizeLight],
      plotOptions: {
        bar: {
          distributed: true,
          columnWidth: "42%",
          borderRadius: 6,
          borderRadiusApplication: "end",
          dataLabels: { position: "top" },
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -22,
        formatter: (val: number) => money.short(val),
        style: { fontSize: "12px", fontWeight: 600, colors: [palette.dataLabel] },
      },
      legend: { show: false },
      states: { active: { filter: { type: "none" } } },
      grid: { borderColor: palette.grid, strokeDashArray: 4, xaxis: { lines: { show: false } }, padding: { top: 8 } },
      xaxis: {
        categories: ["Current Period", "Last Period"],
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { fontSize: "12px" } },
        tooltip: { enabled: false },
      },
      yaxis: {
        min: 0,
        ...niceScale(avgDealMax * 1.2),
        labels: { formatter: (val: number) => formatAxisValue(val), style: { fontSize: "11px" } },
      },
      tooltip: {
        marker: { show: false },
        y: { title: { formatter: () => `Avg ${names.deal} Size` }, formatter: (val: number) => formatAmount(val) },
      },
    }),
    [avgDealMax, money, formatAmount, names.deal]
  );

  const growthPoints = useMemo(() => data?.salesGrowth[growthKey] ?? [], [data, growthKey]);
  const growthSeries = useMemo(
    () => [
      { name: "Sales", type: "area", data: growthPoints.map((point) => point.actual) },
      { name: "Prior Period", type: "line", data: growthPoints.map((point) => point.prior) },
    ],
    [growthPoints]
  );
  const growthOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        id: GROWTH_CHART_ID,
        type: "line",
        toolbar: { show: false },
        zoom: { enabled: true, type: "x", allowMouseWheelZoom: false },
        foreColor: palette.axis,
        fontFamily: TEMPLATE_FONT_FAMILY,
      },
      colors: [palette.primary, palette.secondary],
      stroke: { curve: "smooth", width: [2, 2], dashArray: [0, 5] },
      fill: {
        type: ["gradient", "solid"],
        opacity: [1, 1],
        gradient: {
          type: "vertical",
          shade: "light",
          shadeIntensity: 0,
          inverseColors: false,
          gradientToColors: [palette.primary, palette.secondary],
          opacityFrom: 0.42,
          opacityTo: 0.12,
          stops: [0, 100],
        },
      },
      dataLabels: { enabled: false },
      markers: { size: 0, hover: { size: 4 } },
      legend: {
        show: true,
        position: "top",
        horizontalAlign: "right",
        fontSize: "13px",
        markers: { size: 5 },
        itemMargin: { horizontal: 8 },
      },
      grid: { borderColor: palette.grid, strokeDashArray: 4, xaxis: { lines: { show: false } } },
      xaxis: { ...categoryXAxis(pointLabels(growthPoints, growthKey)), axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: {
        min: 0,
        ...niceScale(Math.max(0, ...growthPoints.map((point) => Math.max(point.actual, point.prior)))),
        labels: { formatter: (val: number) => formatAxisValue(val), style: { fontSize: "11px" } },
      },
      tooltip: { shared: true, intersect: false, y: { formatter: (val: number) => formatAmount(val) } },
    }),
    [growthPoints, growthKey, formatAmount]
  );

  const header = (
    <>
      <div
        className={`d-flex align-items-center justify-content-between gap-2 flex-wrap ${hasFilterChips(filters, defaultFilters, true) ? "mb-3" : "mb-4"
          }`}
      >
        <h4 className="ss-page-title mb-0">Sales Dashboard</h4>
        <div className="gap-2 d-flex align-items-center flex-wrap">
          {selectedTenantId && (
            <DashboardFilterDropdown
              title="Sales Filters"
              filters={filters}
              onChange={setFilters}
              loading={loading}
              showDefaultDateRange
            />
          )}
          <ExportDropdown
            disabled={!data || loading}
            exporting={exporting}
            spinClassName="ss-spin"
            onExport={handleExport}
          />
          <HeaderAction label="Refresh" icon={`ti ti-refresh${loading ? " ss-spin" : ""}`} onClick={reload} />
          <HeaderAction
            label={headerCollapse ? "Expand" : "Collapse"}
            icon="ti ti-transition-top"
            onClick={() => dispatch(setHeaderCollapse(!headerCollapse))}
          />
        </div>
      </div>
      <ActiveFilterChips filters={filters} onChange={setFilters} disabled={loading} showDefaultDateRange />
    </>
  );

  if (selectedTenantId && (loading || (!data && !error))) {
    return (
      <div className="page-wrapper sales-summary">
        <div className="content pb-0" aria-busy="true">
          {header}
          <span className="visually-hidden" role="status">
            Loading sales overview
          </span>
          <SalesSummarySkeleton />
        </div>
      </div>
    );
  }

  if (!selectedTenantId || !data) {
    return (
      <div className="page-wrapper sales-summary">
        <div className="content pb-0">
          {header}
          <div className="card">
            <div className="card-body text-center py-5">
              {!selectedTenantId ? (
                <>
                  <i className="ti ti-building-community fs-1 text-muted mb-3" />
                  <h5>Select a Tenant</h5>
                  <p className="text-muted mb-0">Please select a tenant to view the sales dashboard.</p>
                </>
              ) : (
                <>
                  <i className="ti ti-alert-circle fs-1 text-danger mb-3" />
                  <h5>Unable to load sales overview</h5>
                  <p className="text-muted mb-3">{error}</p>
                  <button type="button" className="btn btn-outline-light" onClick={reload}>
                    <i className="ti ti-refresh me-1" />
                    Try Again
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper sales-summary">
      <div className="content pb-0">
        {header}

        {error && (
          <div className="alert alert-danger d-flex align-items-center justify-content-between gap-2 mb-4">
            <span>{error}</span>
            <button type="button" className="btn btn-sm btn-outline-light" onClick={reload}>
              Try Again
            </button>
          </div>
        )}

        <div className="ss-body" ref={reportRef}>
          {stats.length > 0 && (
            <div className="ss-pdf-section">
              <div className="mb-2">
                <h6 className="ss-section-title mb-0">Reports &amp; Analytics</h6>
                <p className="text-muted fs-12 mb-0">Current period compared with the previous period</p>
              </div>
              <div className="row g-3 mb-4">
                {stats.map((stat) => {
                  const meta = overviewStatMeta[stat.key] ?? defaultStatMeta;
                  return (
                    <div className="col-12 col-md-6 col-xl d-flex" key={stat.key}>
                      <div className="card flex-fill mb-0">
                        <div className="card-body">
                          <span className={`ss-stat-icon bg-soft-${meta.tone} text-${meta.tone} mb-2`}>
                            <i className={statIcon(meta, money)} />
                          </span>
                          <div className="fs-12 text-muted">{meta.label ? meta.label(names) : stat.displayName}</div>
                          <div className="fs-20 fw-bold text-dark">{formatStatValue(stat, meta, money)}</div>
                          <div className="fs-12 text-muted d-flex align-items-center flex-wrap gap-1">
                            {meta.showAmount && stat.amount !== null && (
                              <span className="text-dark fw-medium">{money.short(stat.amount)} ·</span>
                            )}
                            <span className={growthTextClass(stat.growth)}>{formatGrowth(stat.growth)}</span>
                            <span>vs last period</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="row ss-pdf-section">
            <div className="col-xxl-8 col-xl-12 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-start justify-content-between flex-wrap row-gap-3 mb-4">
                    <div>
                      <h5 className="sub-title mb-1">Sales Trend</h5>
                      <p className="ss-card-subtitle mb-0">{rangeLabel}</p>
                    </div>
                    <GranularityToggle value={salesKey} onChange={setSalesKey} />
                  </div>
                  <SalesTotalBlock
                    value={formatAmount(totalSales?.amount ?? 0)}
                    growth={totalSales?.growth ?? ""}
                    chart={
                      salesBars.values.length ? (
                        <Chart
                          key={salesKey}
                          options={salesBars.options}
                          series={[{ name: "Sales", data: salesBars.values }]}
                          type="bar"
                          width="100%"
                          height={180}
                        />
                      ) : (
                        <div className="h-100 d-flex align-items-center justify-content-center text-muted fs-13">
                          No sales recorded for this period
                        </div>
                      )
                    }
                  />
                </div>
              </div>
            </div>

            <div className="col-xxl-4 col-xl-12 d-flex">
              <div className="card flex-fill">
                <div className="card-body d-flex flex-column">
                  <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                    <div>
                      <h5 className="sub-title mb-1">Conversion Rate</h5>
                      <p className="ss-card-subtitle mb-0">{rangeLabel}</p>
                    </div>
                    <OptionDropdown value={conversionKey} options={conversionOptions} onChange={setConversionKey} />
                  </div>
                  <div className="flex-fill d-flex align-items-center justify-content-center">
                    <ConversionGauge value={conversionValue} />
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap mt-3">
                    <h3 className="ss-rate mb-0">{trimDecimals(conversionValue, 1)}%</h3>
                    <p className="mb-0">of {conversionLabel.toLowerCase()} converted</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row ss-pdf-section">
            <div className="col-xl-6 col-md-12 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                    <div>
                      <h5 className="sub-title mb-1">{names.deals} Won Vs Lost</h5>
                      <p className="mb-0">
                        {countFormatter.format(closedCount)} closed {names.deals.toLowerCase()}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-icon btn-outline-light shadow"
                      aria-label="Refresh"
                      onClick={reload}
                    >
                      <i className="ti ti-refresh" />
                    </button>
                  </div>
                  <div className="d-flex align-items-center flex-wrap flex-md-nowrap gap-3">
                    <div className="flex-fill min-w-0 w-100">
                      <div className="border rounded p-3 d-flex align-items-center mb-3">
                        <span className="ss-deal-icon ss-deal-icon-won me-3">
                          <i className="ti ti-tag" />
                        </span>
                        <div>
                          <p className="text-dark fw-medium mb-1">{names.deals} Won</p>
                          <div className="d-flex align-items-center gap-1 flex-wrap">
                            <h3 className="custom-title mb-0 me-1">{countFormatter.format(wonLost?.won.count ?? 0)}</h3>
                            <p className="fs-12 mb-0">
                              <span className={growthTextClass(wonLost?.won.growth)}>
                                {formatGrowth(wonLost?.won.growth)}
                              </span>{" "}
                              Last Period
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="border rounded p-3 d-flex align-items-center">
                        <span className="ss-deal-icon ss-deal-icon-lost me-3">
                          <i className="ti ti-tag-off" />
                        </span>
                        <div>
                          <p className="text-dark fw-medium mb-1">{names.deals} Lost</p>
                          <div className="d-flex align-items-center gap-1 flex-wrap">
                            <h3 className="custom-title ss-lost-count mb-0 me-1">
                              {countFormatter.format(wonLost?.lost.count ?? 0)}
                            </h3>
                            <p className="fs-12 mb-0">
                              <span className={growthTextClass(wonLost?.lost.growth)}>
                                {formatGrowth(wonLost?.lost.growth)}
                              </span>{" "}
                              Last Period
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="ss-deals-chart d-flex align-items-center justify-content-center">
                      <DealsRings won={wonLost?.won.count ?? 0} lost={wonLost?.lost.count ?? 0} names={names} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-6 col-md-12 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <h5 className="sub-title mb-3">Sales Overview by {names.deal} Stages</h5>
                  <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
                    <h3 className="custom-title mb-0">{formatAmount(pipeline?.total ?? 0)}</h3>
                    <p className="fs-12 mb-0">
                      {countFormatter.format(pipeline?.dealCount ?? 0)} {names.deals.toLowerCase()}
                    </p>
                  </div>
                  {pipeline?.stages.length ? (
                    pipeline.stages.map((stage, index) => (
                      <div
                        key={`${stage.name}-${index}`}
                        className={`ss-stage${index < pipeline.stages.length - 1 ? " mb-2" : ""}`}
                        role="progressbar"
                        aria-valuenow={Math.round(stageWidths[index])}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <div
                          className={`ss-stage-fill ${stageClassNames[index % stageClassNames.length]}`}
                          style={{ width: `${stageWidths[index]}%` }}
                        />
                        <span className="ss-stage-label">
                          {stage.name} - {formatDealValue(stage.value)} ({countFormatter.format(stage.dealCount)})
                        </span>
                      </div>
                    ))
                  ) : (
                    <EmptyState message={`No ${names.pipeline} stages to display`} />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row ss-pdf-section">
            <div className="col-xl-7 col-md-12 d-flex">
              <div className="card flex-fill">
                <div className="card-body ss-deals-heading d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <h5 className="sub-title mb-0">Recently Created {names.deals}</h5>
                  <Link className="btn btn-sm btn-outline-light shadow" to={route.deals}>
                    View All
                  </Link>
                </div>
                {deals.length ? (
                  <div className="table-responsive">
                    <table className="table ss-deals-table mb-0">
                      <thead>
                        <tr>
                          <th>{names.deals}</th>
                          <th>Owner</th>
                          <th className="text-end">Value</th>
                          <th className="ss-status-col">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deals.map((deal, index) => (
                          <tr key={`${deal.dealId ?? index}`}>
                            <td>
                              <DealTitle deal={deal} names={names} />
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <PersonAvatar
                                  name={deal.ownerName || "Unassigned"}
                                  url={deal.ownerAvatarUrl}
                                  sizeClass="avatar-xs me-2"
                                />
                                <span className="text-dark text-nowrap">{deal.ownerName || "Unassigned"}</span>
                              </div>
                            </td>
                            <td className="text-end text-dark">{formatDealValue(deal.value)}</td>
                            <td>
                              <span className={`badge ${statusClassName(deal.status)}`}>{deal.status || "Open"}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyState message={`No ${names.deals.toLowerCase()} created in this period`} />
                )}
              </div>
            </div>

            <div className="col-xl-5 col-md-12 d-flex">
              <div className="card flex-fill">
                <div className="card-body d-flex flex-column">
                  <h5 className="sub-title mb-3">Avg {names.deal} Size</h5>
                  <div className="d-flex align-items-center gap-2 flex-wrap mb-4">
                    <h3 className="custom-title mb-0">{formatAmount(avgDeal?.current ?? 0)}</h3>
                    <span className={`badge rounded-pill ${growthBadgeClass(avgDeal?.growth)}`}>
                      {formatGrowth(avgDeal?.growth)}
                    </span>
                    <p className="fs-12 mb-0">vs Last Period</p>
                  </div>
                  <div className="ss-avg-chart">
                    <div className="ss-avg-chart-inner">
                      <Chart
                        options={avgDealOptions}
                        series={[{ name: `Avg ${names.deal} Size`, data: [avgDeal?.current ?? 0, avgDeal?.previous ?? 0] }]}
                        type="bar"
                        height="100%"
                      />
                    </div>
                  </div>
                  <div className="row g-2 mt-2">
                    {avgDealTiles.map((tile) => (
                      <div className="col-4" key={tile.key}>
                        <div className="ss-avg-tile h-100">
                          <p className="fs-12 text-muted mb-1 text-truncate" title={tile.label}>
                            {tile.label}
                          </p>
                          <p className={`fs-14 fw-semibold mb-0 text-truncate ${tile.className}`}>{tile.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row ss-pdf-section">
            <div className="col-md-12 d-flex">
              <div className="card flex-fill">
                <div className="card-body pb-0">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                    <h5 className="sub-title mb-0">Sales Growth</h5>
                    <GranularityToggle value={growthKey} onChange={setGrowthKey} />
                  </div>
                  {growthPoints.length ? (
                    <div ref={growthZoomRef}>
                      <Chart options={growthOptions} series={growthSeries} type="line" height={340} />
                    </div>
                  ) : (
                    <EmptyState message="No sales growth data for this period" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesSummary;
